
import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { motion } from 'framer-motion';
import { useToast } from '@/components/ui/use-toast';
import { Search, MapPin, Map } from 'lucide-react';

interface SearchBarProps {
  onSearch: (postalCode: string, city: string, radius: number) => void;
  isSearching?: boolean;
}

export const SearchBar: React.FC<SearchBarProps> = ({ onSearch, isSearching = false }) => {
  const { t, language } = useLanguage();
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [radius, setRadius] = useState(50); // Default radius is 50km
  const { toast } = useToast();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Allow users to search by city only, so we don't need to check postal code
    if (!postalCode.trim() && !city.trim()) {
      toast({
        title: t('searchError'),
        description: t('providePostalCodeOrCity'),
        variant: 'destructive',
      });
      return;
    }
    
    console.log(`Initiating search with postal code: ${postalCode.trim()}, city: ${city.trim()}, radius: ${radius}km`);
    onSearch(postalCode.trim(), city.trim(), radius);
    
    toast({
      title: t('searching'),
      description: getSearchDescription(postalCode.trim(), city.trim(), radius),
      variant: 'default',
    });
  };

  // Log when radius changes to help with debugging
  useEffect(() => {
    console.log(`Search radius updated to: ${radius}km`);
  }, [radius]);

  const getSearchDescription = (postal: string, cityName: string, rad: number) => {
    if (postal && cityName) {
      return `${t('searchingPostalCode')}: ${postal}, ${t('searchingCity')}: ${cityName}, ${t('searchRadius')}: ${rad}km`;
    } else if (postal) {
      return `${t('searchingPostalCode')}: ${postal}, ${t('searchRadius')}: ${rad}km`;
    } else if (cityName) {
      return `${t('searchingCity')}: ${cityName}, ${t('searchRadius')}: ${rad}km`;
    } else {
      return `${t('searchingAllSchools')}, ${t('searchRadius')}: ${rad}km`;
    }
  };

  // Define different color classes for the radius scale
  const getRadiusColorClass = () => {
    if (radius <= 10) return "text-green-600";
    if (radius <= 50) return "text-amber-600";
    return "text-red-600";
  };

  // Get appropriate label for the radius slider based on the current value
  const getRadiusLabel = () => {
    if (radius <= 10) return t('smallRadius');
    if (radius <= 50) return t('mediumRadius');
    if (radius <= 100) return t('largeRadius');
    return t('veryLargeRadius');
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full bg-white rounded-lg shadow-md p-6 mb-10"
    >
      <form onSubmit={handleSearch} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="col-span-1">
            <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
              {t('postalCode')}
            </label>
            <div className="relative">
              <Input
                id="postalCode"
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder={t('enterPostalCode')}
                className="w-full transition-all focus:ring-2 focus:ring-primary/50 pl-10"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>

          <div className="col-span-1">
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              {t('city')}
            </label>
            <div className="relative">
              <Input
                id="city"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={t('enterCity')}
                className="w-full transition-all focus:ring-2 focus:ring-primary/50 pl-10"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <MapPin className="h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>

          <div className="col-span-1">
            <label htmlFor="radius" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <Map className="h-4 w-4 mr-1" />
              <span>{t('searchRadius')}</span>
              <span className={`ml-2 font-bold ${getRadiusColorClass()}`}>({radius} km)</span>
            </label>
            <div className="px-2">
              <Slider
                id="radius"
                min={1}
                max={200}
                step={1}
                value={[radius]}
                onValueChange={(value) => setRadius(value[0])}
                className="my-4"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1km</span>
                <span>50km</span>
                <span>100km</span>
                <span>200km</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              type="submit"
              className="bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-md transition-all"
              disabled={isSearching}
            >
              {isSearching ? t('searching') : t('searchButton')}
            </Button>
          </motion.div>
        </div>
      </form>
    </motion.div>
  );
};
